<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  OBLIGATION_KINDS,
  createObligation,
  deleteObligation,
  deletePayment,
  getObligation,
  getSchedule,
  listObligations,
  listPayments,
  money,
  recordPayment,
  simulate,
  updateObligation,
  type Obligation,
  type ObligationPayment,
  type ScheduleRow,
  type SimulationResult,
  type Strategy,
} from '../api/planning'
import type { ApiError } from '../api/http'
import { useSession } from '../stores/session'
import { compact, useErrorText, useForm } from '../lib/form'
import { formatMoney, isoDaysAgo } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import UiDrawer from '../components/UiDrawer.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { t, locale } = useI18n()
const session = useSession()
const errText = useErrorText()
const load = useForm()
const create = useForm()
const action = useForm()

const items = ref<Obligation[]>([])
const showArchived = ref(false)
const visible = computed(() => items.value.filter((o) => showArchived.value || !o.archived))
watch(() => session.workspace?.id, () => load.submit(async () => (items.value = await listObligations())), { immediate: true })

const fmt = (m: { amount_minor: number; currency: string }) => formatMoney(m, locale.value)

// ---- create ----
const showCreate = ref(false)
const blank = () => ({ name: '', kind: 'loan' as Obligation['kind'], lender: '', principal: '', currency: 'EUR', annual_rate_pct: '', start_date: isoDaysAgo(0), term_months: '', monthly_payment: '' })
const form = reactive(blank())
async function onCreate() {
  const cur = form.currency
  const ok = await create.submit(async () => {
    const o = await createObligation(
      compact({
        name: form.name,
        kind: form.kind,
        lender: form.lender,
        principal: money(form.principal, cur),
        annual_rate_pct: form.annual_rate_pct.replace(',', '.'),
        start_date: form.start_date,
        ...(form.term_months ? { term_months: Number(form.term_months) } : {}),
        ...(form.monthly_payment ? { monthly_payment: money(form.monthly_payment, cur) } : {}),
      }),
    )
    items.value = [...items.value, o]
  })
  if (ok) {
    Object.assign(form, blank())
    showCreate.value = false
  }
}

// ---- detail ----
const selected = ref<Obligation | null>(null)
const schedule = ref<ScheduleRow[]>([])
const payments = ref<ObligationPayment[]>([])
const scheduleErr = ref('')
const pay = reactive({ date: isoDaysAgo(0), amount: '', note: '' })
const detail = useForm()

function replace(o: Obligation) {
  items.value = items.value.map((x) => (x.id === o.id ? o : x))
  selected.value = o
}
async function loadDetail(o: Obligation) {
  scheduleErr.value = ''
  const [p, s] = await Promise.all([listPayments(o.id), getSchedule(o.id).catch((e: ApiError) => {
      scheduleErr.value = errText(e) // e.g. never_pays_off: the rest of the detail still shows
      return [] as ScheduleRow[]
    }),
  ])
  payments.value = p
  schedule.value = s
}
async function open(o: Obligation) {
  selected.value = o
  schedule.value = []
  payments.value = []
  Object.assign(pay, { date: isoDaysAgo(0), amount: '', note: '' })
  await detail.submit(() => loadDetail(o))
}
async function refresh(o: Obligation) {
  replace(await getObligation(o.id))
  await loadDetail(o)
}
async function onPay(o: Obligation) {
  const ok = await action.submit(async () => {
    replace(await recordPayment(o.id, compact({ date: pay.date, amount: money(pay.amount, o.monthly_payment.currency), note: pay.note })))
    await loadDetail(o)
  })
  if (ok) Object.assign(pay, { amount: '', note: '' })
}
const removePayment = (o: Obligation, p: ObligationPayment) =>
  action.submit(async () => {
    await deletePayment(o.id, p.id)
    await refresh(o)
  })
const toggleArchive = (o: Obligation) => action.submit(async () => replace(await updateObligation(o.id, { archived: !o.archived })))
async function remove(o: Obligation) {
  if (!confirm(t('obligations.deleteConfirm', { name: o.name }))) return
  if (await action.submit(() => deleteObligation(o.id))) {
    items.value = items.value.filter((x) => x.id !== o.id)
    selected.value = null
  }
}

// ---- simulator ----
const sim = reactive({ extra: '100', currency: '', strategy: 'avalanche' as Strategy })
const simResult = ref<SimulationResult | null>(null)
const simForm = useForm()
const currencies = computed(() => [...new Set(items.value.filter((o) => !o.archived).map((o) => o.balance.currency))])
const simCurrency = computed(() => sim.currency || currencies.value[0] || 'EUR')
const runSim = () => simForm.submit(async () => (simResult.value = await simulate(sim.extra, simCurrency.value, sim.strategy)))
const minus = (a: { amount_minor: number; currency: string }, b: { amount_minor: number }) => ({ ...a, amount_minor: a.amount_minor - b.amount_minor })
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('obligations.title') }}</h1>
        <p class="muted">{{ $t('obligations.subtitle') }}</p>
      </div>
      <UiButton v-if="session.canWrite && !showCreate" data-testid="new-obligation" @click="showCreate = true">{{ $t('obligations.new') }}</UiButton>
    </header>

    <UiCard v-if="showCreate" :title="$t('obligations.new')">
      <form class="grid-2" data-testid="create-obligation" @submit.prevent="onCreate">
        <UiField :label="$t('obligations.name')" :error="create.fieldErr('name')">
          <input v-model.trim="form.name" name="name" required maxlength="100" />
        </UiField>
        <UiField :label="$t('obligations.kind')" :error="create.fieldErr('kind')">
          <select v-model="form.kind" name="kind">
            <option v-for="k in OBLIGATION_KINDS" :key="k" :value="k">{{ $t(`obligations.kinds.${k}`) }}</option>
          </select>
        </UiField>
        <UiField :label="$t('obligations.lender')" :error="create.fieldErr('lender')">
          <input v-model.trim="form.lender" name="lender" maxlength="100" />
        </UiField>
        <UiField :label="$t('obligations.principal')" :error="create.fieldErr('principal.amount_minor')">
          <input v-model.trim="form.principal" name="principal" required inputmode="decimal" pattern="\d+([.,]\d{1,2})?" />
        </UiField>
        <UiField :label="$t('accounts.currency')" :error="create.fieldErr('principal.currency')">
          <input v-model.trim="form.currency" name="currency" required pattern="[A-Za-z]{3}" maxlength="3" />
        </UiField>
        <UiField :label="$t('obligations.rate')" :error="create.fieldErr('annual_rate_pct')">
          <input v-model.trim="form.annual_rate_pct" name="annual_rate_pct" required inputmode="decimal" pattern="\d{1,3}([.,]\d{1,4})?" />
        </UiField>
        <UiField :label="$t('obligations.startDate')" :error="create.fieldErr('start_date')">
          <input v-model="form.start_date" name="start_date" type="date" required />
        </UiField>
        <UiField :label="$t('obligations.term')" :error="create.fieldErr('term_months')" :hint="$t('obligations.termHint')">
          <input v-model.trim="form.term_months" name="term_months" type="number" min="1" max="600" />
        </UiField>
        <UiField :label="$t('obligations.monthlyPayment')" :error="create.fieldErr('monthly_payment.amount_minor')" :hint="$t('obligations.paymentHint')">
          <input v-model.trim="form.monthly_payment" name="monthly_payment" inputmode="decimal" pattern="\d+([.,]\d{1,2})?" />
        </UiField>
        <div class="row">
          <UiButton type="submit" :loading="create.busy.value">{{ $t('common.create') }}</UiButton>
          <UiButton variant="ghost" @click="showCreate = false">{{ $t('common.cancel') }}</UiButton>
        </div>
      </form>
      <ErrorBanner :error="create.error.value" />
    </UiCard>

    <label class="row small"><input v-model="showArchived" type="checkbox" class="auto" /> {{ $t('savings.showArchived') }}</label>
    <ErrorBanner :error="load.error.value" />

    <UiCard>
      <p v-if="load.busy.value && !items.length" class="muted">{{ $t('common.loading') }}</p>
      <EmptyState v-else-if="!visible.length" :title="$t('obligations.empty')" :message="$t('obligations.emptyText')" />
      <div v-else class="table-wrap">
        <table class="table" data-testid="obligation-table">
          <thead>
            <tr>
              <th>{{ $t('obligations.name') }}</th>
              <th>{{ $t('obligations.kind') }}</th>
              <th class="num">{{ $t('obligations.balance') }}</th>
              <th class="num">{{ $t('obligations.monthlyPayment') }}</th>
              <th>{{ $t('obligations.payoffDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in visible" :key="o.id" class="clickable" data-testid="obligation-row" @click="open(o)">
              <td>
                <strong>{{ o.name }}</strong>
                <UiBadge v-if="o.archived" variant="outline">{{ $t('savings.archived') }}</UiBadge>
                <div class="muted small">{{ o.lender ?? '' }} {{ o.annual_rate_pct }}%</div>
              </td>
              <td>{{ $t(`obligations.kinds.${o.kind}`) }}</td>
              <td class="num">{{ fmt(o.balance) }}</td>
              <td class="num" data-testid="obligation-payment">{{ fmt(o.monthly_payment) }}</td>
              <td>{{ o.payoff_date ?? $t('obligations.never') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </UiCard>

    <UiCard v-if="currencies.length" :title="$t('obligations.simulator')">
      <form class="row" data-testid="simulator" @submit.prevent="runSim">
        <UiField :label="$t('obligations.extra')">
          <input v-model.trim="sim.extra" name="extra" class="amt" required inputmode="decimal" pattern="\d+([.,]\d{1,2})?" />
        </UiField>
        <UiField v-if="currencies.length > 1" :label="$t('accounts.currency')">
          <select v-model="sim.currency" class="auto">
            <option v-for="c in currencies" :key="c" :value="c">{{ c }}</option>
          </select>
        </UiField>
        <UiField :label="$t('obligations.strategy')">
          <select v-model="sim.strategy" name="strategy" class="auto">
            <option value="avalanche">{{ $t('obligations.avalanche') }}</option>
            <option value="snowball">{{ $t('obligations.snowball') }}</option>
          </select>
        </UiField>
        <UiButton type="submit" :loading="simForm.busy.value" data-testid="simulate">{{ $t('obligations.simulate') }}</UiButton>
      </form>
      <p class="small muted">{{ $t(`obligations.${sim.strategy}Hint`) }}</p>
      <ErrorBanner :error="simForm.error.value" />
      <template v-if="simResult">
        <div class="grid-2">
          <div>
            <p class="muted small">{{ $t('obligations.monthsSaved') }}</p>
            <p class="big in" data-testid="sim-months-saved">{{ simResult.months_saved }}</p>
            <p class="small muted">{{ simResult.months }} / {{ simResult.baseline_months }} · {{ simResult.payoff_month ?? '—' }}</p>
          </div>
          <div>
            <p class="muted small">{{ $t('obligations.interestSaved') }}</p>
            <p class="big in" data-testid="sim-interest-saved">{{ fmt(simResult.interest_saved) }}</p>
            <p class="small muted">{{ fmt(simResult.total_interest) }} / {{ fmt(simResult.baseline_interest) }}</p>
          </div>
        </div>
        <div class="table-wrap">
          <table class="table" data-testid="sim-table">
            <thead>
              <tr>
                <th>{{ $t('obligations.name') }}</th>
                <th>{{ $t('obligations.payoff') }}</th>
                <th>{{ $t('obligations.baseline') }}</th>
                <th class="num">{{ $t('obligations.monthsSaved') }}</th>
                <th class="num">{{ $t('obligations.interestSaved') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in simResult.obligations" :key="s.obligation_id">
                <td>{{ s.name }}</td>
                <td>{{ s.payoff_month }} <span class="muted small">({{ s.months }})</span></td>
                <td>{{ s.baseline_payoff_month }} <span class="muted small">({{ s.baseline_months }})</span></td>
                <td class="num">{{ s.baseline_months - s.months }}</td>
                <td class="num">{{ fmt(minus(s.baseline_interest, s.total_interest)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </UiCard>

    <UiDrawer v-if="selected" :title="selected.name" @close="selected = null">
      <ErrorBanner :error="action.error.value ?? detail.error.value" />
      <dl class="kv">
        <dt>{{ $t('obligations.kind') }}</dt>
        <dd>{{ $t(`obligations.kinds.${selected.kind}`) }}{{ selected.lender ? ` · ${selected.lender}` : '' }}</dd>
        <dt>{{ $t('obligations.principal') }}</dt>
        <dd>{{ fmt(selected.principal) }} · {{ selected.annual_rate_pct }}%</dd>
        <dt>{{ $t('obligations.balance') }}</dt>
        <dd data-testid="obligation-balance">{{ fmt(selected.balance) }}</dd>
        <dt>{{ $t('obligations.monthlyPayment') }}</dt>
        <dd>{{ fmt(selected.monthly_payment) }}</dd>
        <dt>{{ $t('obligations.startDate') }}</dt>
        <dd>{{ selected.start_date }}{{ selected.term_months ? ` · ${selected.term_months} ${$t('obligations.months')}` : '' }}</dd>
        <dt>{{ $t('obligations.payoffDate') }}</dt>
        <dd>{{ selected.payoff_date ?? $t('obligations.never') }}</dd>
        <dt>{{ $t('obligations.totalInterest') }}</dt>
        <dd>{{ selected.total_interest ? fmt(selected.total_interest) : '—' }}</dd>
      </dl>

      <div v-if="session.canWrite" class="row">
        <UiButton variant="outline" size="sm" @click="toggleArchive(selected)">{{ selected.archived ? $t('savings.unarchive') : $t('savings.archive') }}</UiButton>
        <UiButton variant="destructive" size="sm" @click="remove(selected)">{{ $t('common.delete') }}</UiButton>
      </div>

      <h3>{{ $t('obligations.payments') }}</h3>
      <form v-if="session.canWrite" class="row" data-testid="record-payment" @submit.prevent="onPay(selected)">
        <input v-model="pay.date" type="date" class="auto" required :max="isoDaysAgo(0)" :min="selected.start_date" :aria-label="$t('savings.date')" />
        <input v-model.trim="pay.amount" class="amt" required inputmode="decimal" pattern="\d+([.,]\d{1,2})?" :placeholder="$t('budget.amount')" :aria-label="$t('budget.amount')" />
        <input v-model.trim="pay.note" class="grow" maxlength="200" :placeholder="$t('bills.note')" :aria-label="$t('bills.note')" />
        <UiButton type="submit" size="sm" :loading="action.busy.value">{{ $t('obligations.recordPayment') }}</UiButton>
      </form>
      <p v-if="!payments.length" class="muted small">—</p>
      <table v-else class="table" data-testid="payment-list">
        <tbody>
          <tr v-for="p in payments" :key="p.id">
            <td>{{ p.date }}</td>
            <td class="small muted">{{ p.note ?? '' }}</td>
            <td class="num">{{ fmt(p.amount) }}</td>
            <td v-if="session.canWrite" class="num">
              <UiButton variant="ghost" size="sm" :aria-label="$t('common.delete')" @click="removePayment(selected, p)">×</UiButton>
            </td>
          </tr>
        </tbody>
      </table>

      <h3>{{ $t('obligations.schedule') }}</h3>
      <p v-if="detail.busy.value" class="muted">{{ $t('common.loading') }}</p>
      <p v-else-if="scheduleErr" class="out small">{{ scheduleErr }}</p>
      <div v-else class="table-wrap">
        <table class="table" data-testid="schedule-table">
          <thead>
            <tr>
              <th class="num">#</th>
              <th>{{ $t('obligations.dueDate') }}</th>
              <th class="num">{{ $t('obligations.payment') }}</th>
              <th class="num">{{ $t('obligations.interest') }}</th>
              <th class="num">{{ $t('obligations.principal') }}</th>
              <th class="num">{{ $t('obligations.balance') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in schedule" :key="r.n" :class="{ past: r.past }" data-testid="schedule-row">
              <td class="num">{{ r.n }}</td>
              <td>
                {{ r.due_date }}
                <UiBadge v-if="r.recorded" variant="success">{{ $t('obligations.recorded') }}</UiBadge>
              </td>
              <td class="num">{{ fmt(r.payment) }}</td>
              <td class="num">{{ fmt(r.interest) }}</td>
              <td class="num">{{ fmt(r.principal) }}</td>
              <td class="num">{{ fmt(r.balance) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </UiDrawer>
  </div>
</template>

<style scoped>
.wide { max-width: 1200px; }
.clickable { cursor: pointer; }
.clickable:hover td { background: var(--bg); }
.past td { color: var(--fg-muted); }
.big { font-size: 1.4rem; font-weight: 600; font-variant-numeric: tabular-nums; }
.in { color: var(--gain); }
.out { color: var(--loss); }
.auto { width: auto; }
.amt { width: 8rem; }
.grow { flex: 1; min-width: 120px; }
</style>
